import axios from 'axios'
import {
  connectOpenCode,
  encodeOpenCodeDirectoryHeader,
  getOpenCodeUserWorkspace,
  resolveOpenCodeWorkspaceDirectory,
} from '@/utils/opencode'

const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

export const getInspirationCases = () =>
  axios.get('/api/inspiration/cases', { headers: getAuthHeaders() })

export const recordCaseView = (id) =>
  axios.post(`/api/inspiration/cases/${id}/view`, null, { headers: getAuthHeaders() })

export const recordCaseLike = (id) =>
  axios.post(`/api/inspiration/cases/${id}/like`, null, { headers: getAuthHeaders() })

export const listAnalysisTemplates = () =>
  axios.get('/api/inspiration/templates', { headers: getAuthHeaders() })

export const getAnalysisTemplate = (slug, packageVersion = '') =>
  axios.get(`/api/inspiration/templates/${encodeURIComponent(slug)}`, {
    headers: getAuthHeaders(),
    ...(packageVersion ? { params: { packageVersion } } : {}),
  })

export const listAnalysisProjects = () =>
  axios.get('/api/inspiration/projects', { headers: getAuthHeaders() })

// Project pickers share the authenticated registry; filesystem browsing still
// uses data/discover and is independent of this list.
export const listAnalysisFolders = async () => {
  const token = localStorage.getItem('token')
  const workspace = await getOpenCodeUserWorkspace(token)
  const response = await axios.get('/opencode/global/projects', {
    params: { scope: 'registered' },
    timeout: 30000,
    headers: {
      ...getAuthHeaders(),
      'x-gutdb-token': token,
      'x-opencode-directory': encodeOpenCodeDirectoryHeader(workspace.directory),
    },
  })
  if (!Array.isArray(response.data?.projects)) throw new Error('项目登记查询失败，请重新加载')
  const prefix = `${workspace.directory}/`
  const children = (response.data?.projects || []).flatMap(project => {
    const ref = typeof project.directory === 'string' && project.directory.startsWith(prefix)
      ? project.directory.slice(prefix.length) : ''
    // eslint-disable-next-line no-control-regex
    return ref && !/[\\/\0]/.test(ref) && ref !== '.' && ref !== '..'
      ? [{ name: project.name, ref }] : []
  })
  return { ...response, data: { code: 200, data: [{
    ref: `PERSONAL::${workspace.username}`, name: '个人工作区', type: 'PERSONAL', children,
  }] } }
}

export const createAnalysisProject = async (name) => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('登录状态已失效，请重新登录')
  const directory = await resolveOpenCodeWorkspaceDirectory(token, { allowUserRoot: true })
  return axios.post(
    '/opencode/global/projects',
    { name, source: 'analysis' },
    {
      headers: {
        'x-gutdb-token': token,
        'x-opencode-directory': encodeOpenCodeDirectoryHeader(directory),
      },
    },
  )
}

export const createAnalysisFolder = async ({ folder, name }) => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('登录状态已失效，请重新登录')
  const parts = String(folder || '').split('/').filter(Boolean)
  if (!parts.length || parts.some(part => part === '.' || part === '..' || part.includes('\\'))) {
    throw new Error('请先进入项目目录')
  }
  if (!name || name === '.' || name === '..' || /[\\/]/.test(name)) {
    throw new Error('文件夹名称无效')
  }
  const projectDirectory = await resolveOpenCodeWorkspaceDirectory(token, { projectFolder: parts[0] })
  const currentDirectory = [projectDirectory, ...parts.slice(1)].join('/')
  return axios.post(
    '/opencode/file/mkdir',
    { name, scope: 'project' },
    {
      headers: {
        'x-gutdb-token': token,
        'x-opencode-directory': encodeOpenCodeDirectoryHeader(currentDirectory),
      },
    },
  )
}

export const scanAnalysisFolder = (payload, options = {}) =>
  axios.post('/api/inspiration/data/scan', payload, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const discoverAnalysisInputs = (payload, options = {}) =>
  axios.post('/api/inspiration/data/discover', payload, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const discoverPurposeAnalysisInputs = (payload, options = {}) =>
  axios.post('/api/inspiration/data/discover-purpose', payload, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const reconcileAnalysisParameterDraft = payload =>
  axios.post('/api/inspiration/data/reconcileParameterDraft', payload, { headers: getAuthHeaders(), timeout: 120000 })

export const getAnalysisFieldValues = (payload, options = {}) =>
  axios.post('/api/inspiration/data/fieldValues', payload, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const validateAnalysisCandidate = (payload, options = {}) =>
  axios.post('/api/inspiration/data/validate', payload, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const validateAnalysisCollection = (payload, options = {}) =>
  axios.post('/api/inspiration/data/validate-collection', payload, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const createStandardAnalysis = (payload) =>
  axios.post('/api/inspiration/analyses', payload, { headers: getAuthHeaders() })

export const listStandardAnalyses = () =>
  axios.get('/api/inspiration/analyses', { headers: getAuthHeaders() })

export const deleteStandardAnalysis = (analysisId) =>
  axios.delete(`/api/inspiration/analyses/${encodeURIComponent(analysisId)}`, { headers: getAuthHeaders() })

export const getStandardAnalysis = (analysisId, options = {}) =>
  axios.get(`/api/inspiration/analyses/${encodeURIComponent(analysisId)}`, {
    headers: getAuthHeaders(),
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const getStandardAnalysisRunLogs = (analysisId, runId, options = {}) =>
  axios.get(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/logs`,
    {
      headers: getAuthHeaders(),
      ...(options.signal ? { signal: options.signal } : {}),
    },
  )

export const getStandardAnalysisInputSummary = (analysisId, options = {}) =>
  axios.get(`/api/inspiration/analyses/${encodeURIComponent(analysisId)}/input-summary`, {
    headers: getAuthHeaders(), timeout: 660000,
    ...(options.signal ? { signal: options.signal } : {}),
  })

export const getStandardAnalysisRunDetails = (analysisId, runId, options = {}) =>
  axios.get(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/details`,
    {
      headers: getAuthHeaders(),
      ...(options.signal ? { signal: options.signal } : {}),
    },
  )

export const cancelStandardAnalysisRun = (analysisId, runId) =>
  axios.post(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/cancel`,
    null,
    { headers: getAuthHeaders() },
  )

export const preflightStandardAnalysisResourceRetry = (analysisId, sourceRunId, payload) =>
  axios.post(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(sourceRunId)}/resource-retry/preflight`,
    payload,
    { headers: getAuthHeaders() },
  )

export const advanceStandardAnalysisResourceRetry = (analysisId, sourceRunId, payload) =>
  axios.post(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(sourceRunId)}/resource-retry`,
    payload,
    { headers: getAuthHeaders() },
  )

export const abandonStandardAnalysisResourceRetry = (analysisId, sourceRunId, payload) =>
  axios.post(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(sourceRunId)}/resource-retry/abandon`,
    payload,
    { headers: getAuthHeaders() },
  )

export const acceptStandardAnalysisRun = (analysisId, runId) =>
  axios.post(`/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/accept`, null, { headers: getAuthHeaders() })

export const syncStandardAnalysisAgentReview = (analysisId, runId, status) =>
  axios.post(
    `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/agent-review`,
    { status },
    { headers: getAuthHeaders() },
  )

export const createStandardAnalysisVersion = (analysisId, parameters) =>
  axios.post(`/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs`, parameters, { headers: getAuthHeaders() })

export const getStandardAnalysisCelltypistCatalog = () =>
  axios.get('/api/inspiration/celltypist-models/catalog', { headers: getAuthHeaders() })

export const prepareStandardAnalysisInteraction = async (analysisId, runId, options = {}) => {
  const pollIntervalMs = Math.max(0, options.pollIntervalMs ?? 1200)
  const timeoutMs = Math.max(1, options.timeoutMs ?? 10 * 60 * 1000)
  const requestTimeoutMs = Math.max(1, options.requestTimeoutMs ?? 30000)
  const signal = options.signal
  const startedAt = Date.now()
  const deadline = startedAt + timeoutMs
  const endpoint = `/api/inspiration/analyses/${encodeURIComponent(analysisId)}/runs/${encodeURIComponent(runId)}/interactive`

  for (;;) {
    if (signal?.aborted) throw abortError()
    const remainingBeforeRequest = deadline - Date.now()
    if (remainingBeforeRequest <= 0) throw interactivePreparationTimeoutError()
    const requestConfig = {
      headers: getAuthHeaders(),
      signal,
      timeout: Math.max(1, Math.min(requestTimeoutMs, remainingBeforeRequest)),
    }
    let response
    try {
      response = await axios.post(endpoint, null, requestConfig)
    } catch (cause) {
      if (signal?.aborted) throw abortError()
      if (Date.now() >= deadline) throw interactivePreparationTimeoutError()
      if (!isRetryableInteractivePreparationError(cause)) throw cause
      const remainingBeforeRetry = deadline - Date.now()
      if (remainingBeforeRetry <= 0) throw interactivePreparationTimeoutError()
      if (pollIntervalMs > 0) {
        await wait(Math.min(pollIntervalMs, remainingBeforeRetry), signal)
      }
      continue
    }
    if (Date.now() >= deadline) throw interactivePreparationTimeoutError()
    const interaction = response.data?.data
    if (!interaction?.state) {
      throw new Error('交互运行准备失败：服务未返回状态')
    }
    if (interaction.state === 'READY' || interaction.state === 'FAILED') {
      return { ...response, data: interaction }
    }
    if (interaction.state !== 'PREPARING') {
      throw new Error(`交互运行准备返回未知状态：${interaction.state}`)
    }
    const remainingBeforeWait = deadline - Date.now()
    if (remainingBeforeWait <= 0) throw interactivePreparationTimeoutError()
    if (pollIntervalMs > 0) {
      await wait(Math.min(pollIntervalMs, remainingBeforeWait), signal)
    }
  }
}

const abortError = () => Object.assign(new Error('请求已取消'), {
  name: 'AbortError', code: 'ERR_CANCELED',
})
const interactivePreparationTimeoutError = () => Object.assign(
  new Error('交互运行准备总等待超时，请稍后重试'),
  { code: 'INTERACTIVE_PREPARATION_TIMEOUT' },
)

const RETRYABLE_INTERACTIVE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504])
const RETRYABLE_INTERACTIVE_CODE = new Set([
  'ECONNABORTED',
  'ECONNRESET',
  'EPIPE',
  'ERR_NETWORK',
  'ETIMEDOUT',
])

const isRetryableInteractivePreparationError = cause => {
  if (RETRYABLE_INTERACTIVE_CODE.has(cause?.code)) return true
  const status = Number(cause?.response?.status)
  if (RETRYABLE_INTERACTIVE_STATUS.has(status)) return true
  return Boolean(cause?.request && !cause?.response)
}

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) return reject(abortError())
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', cancel)
    resolve()
  }, milliseconds)
  const cancel = () => {
    clearTimeout(timer)
    reject(abortError())
  }
  signal?.addEventListener('abort', cancel, { once: true })
})

export const draftStandardAnalysisParameters = (payload, options = {}) =>
  runParameterDraft(payload, options, { projectFolder: options.projectFolder })

// Demo only changes workspace preparation; the parameter-generation contract is shared.
export const draftDemoAnalysisParameters = (payload, options = {}) =>
  runParameterDraft(payload, options, { allowUserRoot: true })

const runParameterDraft = async (payload, options, workspaceOptions) => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('missing GutDB token')
  const pollIntervalMs = Math.max(0, options.pollIntervalMs ?? 1200)
  const timeoutMs = Math.max(1000, options.timeoutMs ?? 10 * 60 * 1000)
  const requestTimeoutMs = Math.max(1000, options.requestTimeoutMs ?? 30000)
  const signal = options.signal
  const startedAt = Date.now()
  const directory = await resolveOpenCodeWorkspaceDirectory(token, workspaceOptions)
  const headers = {
    ...getAuthHeaders(),
    'x-gutdb-token': token,
    'x-opencode-directory': encodeOpenCodeDirectoryHeader(directory),
  }
  const requestConfig = { headers, signal, timeout: requestTimeoutMs }
  const publishProgress = response => {
    if (typeof options.onProgress !== 'function') return
    const job = response?.data || {}
    options.onProgress({
      jobId: job.jobId,
      state: job.state,
      phase: job.phase,
      checkpoints: Array.isArray(job.checkpoints)
        ? job.checkpoints.map(checkpoint => ({
            id: checkpoint.id,
            label: checkpoint.label,
            state: checkpoint.state,
          }))
        : [],
    })
  }

  if (signal?.aborted) throw abortError()
  const created = await axios.post('/opencode/standard-analysis/drafts', payload, requestConfig)
  const jobId = created.data?.jobId
  if (!jobId) throw new Error('Agent 参数草案任务创建失败：服务未返回任务编号')
  publishProgress(created)

  let statusResponse = created
  for (;;) {
    const job = statusResponse.data || {}
    if (job.state === 'SUCCEEDED' && job.result) {
      return { ...statusResponse, data: job.result }
    }
    if (job.state === 'FAILED') {
      throw new Error(job.error || 'Agent 参数草案生成失败')
    }
    if (job.state !== 'RUNNING' && job.state !== 'SUCCEEDED') {
      throw new Error(`Agent 参数草案任务返回未知状态：${job.state || 'EMPTY'}`)
    }
    if (Date.now() - startedAt >= timeoutMs) {
      throw new Error('Agent 参数草案等待超时，请稍后重试')
    }
    if (pollIntervalMs > 0) await wait(pollIntervalMs, signal)
    statusResponse = await axios.get(`/opencode/standard-analysis/drafts/${encodeURIComponent(jobId)}`, requestConfig)
    publishProgress(statusResponse)
  }
}


const interpretationRequestConfig = (options = {}) => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('missing GutDB token')
  const requestTimeoutMs = Math.max(1000, options.requestTimeoutMs ?? 30000)
  return {
    token,
    config: {
      headers: { ...getAuthHeaders(), 'x-gutdb-token': token },
      signal: options.signal,
      timeout: requestTimeoutMs,
    },
  }
}

export const createStandardAnalysisInterpretation = async (payload, options = {}) => {
  const { token, config } = interpretationRequestConfig(options)
  const directory = await resolveOpenCodeWorkspaceDirectory(token, {
    workspacePath: payload.outputRef,
    allowUserRoot: true,
  })
  config.headers['x-opencode-directory'] = encodeOpenCodeDirectoryHeader(directory)
  if (options.signal?.aborted) throw abortError()
  const response = await axios.post('/opencode/standard-analysis/interpret', payload, config)
  if (!response.data?.jobId || !['RUNNING', 'SUCCEEDED'].includes(response.data?.state)) {
    throw new Error('Result interpretation job creation returned an invalid response')
  }
  return response
}

export const getStandardAnalysisInterpretationJob = (jobId, options = {}) => {
  const { config } = interpretationRequestConfig(options)
  return axios.get(
    `/opencode/standard-analysis/interpret/${encodeURIComponent(jobId)}`,
    config,
  )
}

export const getStandardAnalysisInterpretationForRun = async (analysisId, runId, options = {}) => {
  const { token, config } = interpretationRequestConfig(options)
  await connectOpenCode(token)
  if (options.signal?.aborted) throw abortError()
  const response = await axios.get('/opencode/standard-analysis/interpret/by-run', {
    ...config,
    params: { analysisId, runId },
  })
  const data = response.data || {}
  const supplied = data.capability && typeof data.capability === 'object'
    ? data.capability
    : undefined
  const supported = typeof supplied?.supported === 'boolean'
    ? supplied.supported
    : typeof data.supported === 'boolean'
    ? data.supported
    : Boolean(data.jobId) || data.supported === undefined
  const capability = {
    supported,
    mode: supplied?.mode || 'UNSUPPORTED',
    code: supplied?.code || (data.supported === undefined ? 'LEGACY_UNKNOWN' : supported ? 'READY' : 'NOT_DECLARED'),
    message: supplied?.message || '',
    ...(supplied?.contractVersion ? { contractVersion: supplied.contractVersion } : {}),
  }
  return {
    ...response,
    data: { ...data, supported: capability.supported, capability },
  }
}
export const startStandardAnalysisInterpretation = async (payload, options = {}) => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('missing GutDB token')
  const pollIntervalMs = Math.max(0, options.pollIntervalMs ?? 1200)
  const timeoutMs = Math.max(1000, options.timeoutMs ?? 22 * 60 * 1000)
  const requestTimeoutMs = Math.max(1000, options.requestTimeoutMs ?? 30000)
  const signal = options.signal
  const startedAt = Date.now()
  const directory = await resolveOpenCodeWorkspaceDirectory(token, {
    workspacePath: payload.outputRef,
    allowUserRoot: true,
  })
  const headers = {
    ...getAuthHeaders(),
    'x-gutdb-token': token,
    'x-opencode-directory': encodeOpenCodeDirectoryHeader(directory),
  }
  const requestConfig = { headers, signal, timeout: requestTimeoutMs }

  if (signal?.aborted) throw abortError()
  const created = await axios.post('/opencode/standard-analysis/interpret', payload, requestConfig)
  const jobId = created.data?.jobId
  if (!jobId) throw new Error('结果解读任务创建失败：服务未返回任务编号')

  let statusResponse = created
  for (;;) {
    const job = statusResponse.data || {}
    if (job.state === 'SUCCEEDED' && job.result) {
      return { ...statusResponse, data: job }
    }
    if (job.state === 'FAILED') {
      throw new Error(job.error || '结果解读生成失败')
    }
    if (job.state !== 'RUNNING' && job.state !== 'SUCCEEDED') {
      throw new Error(`结果解读任务返回未知状态：${job.state || 'EMPTY'}`)
    }
    if (Date.now() - startedAt >= timeoutMs) {
      throw new Error('结果解读等待超时，请稍后重试')
    }
    if (pollIntervalMs > 0) await wait(pollIntervalMs, signal)
    statusResponse = await axios.get(`/opencode/standard-analysis/interpret/${encodeURIComponent(jobId)}`, requestConfig)
  }
}

export const openMarimoResult = async (path, options = {}) => {
  const token = localStorage.getItem('token')
  if (!token) throw new Error('登录状态已失效，请重新登录')
  const signal = options.signal
  const deadline = Date.now() + Math.max(1, options.timeoutMs ?? 10 * 60 * 1000)
  const requestTimeoutMs = Math.max(1, options.requestTimeoutMs ?? 30000)
  const pollIntervalMs = Math.max(0, options.pollIntervalMs ?? 1200)
  const startupTimeoutError = () => Object.assign(
    new Error('Notebook 启动等待超时，请稍后重试；分析结果已保留'),
    { code: 'NOTEBOOK_STARTUP_TIMEOUT' },
  )
  const checkWaiting = () => {
    if (signal?.aborted) throw abortError()
    if (Date.now() >= deadline) throw startupTimeoutError()
  }
  checkWaiting()
  // Connecting the user instance and resolving its workspace are part of
  // Notebook startup too. A dropped bootstrap response must not bypass the
  // same bounded recovery used for the session request below.
  let directory
  while (!directory) {
    checkWaiting()
    let timer
    let cancel
    try {
      directory = await Promise.race([
        resolveOpenCodeWorkspaceDirectory(token, { workspacePath: path, allowUserRoot: true }),
        new Promise((resolve, reject) => {
          cancel = () => reject(abortError())
          if (signal?.aborted) return cancel()
          signal?.addEventListener('abort', cancel, { once: true })
          timer = setTimeout(() => reject(startupTimeoutError()), Math.max(1, deadline - Date.now()))
        }),
      ])
    } catch (cause) {
      checkWaiting()
      if (!isRetryableNotebookTransportError(cause)) throw cause
      options.onRuntime?.({ state: 'recovering' })
    } finally {
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
    }
    if (!directory && pollIntervalMs > 0) await wait(Math.min(pollIntervalMs, deadline - Date.now()), signal)
  }
  const headers = {
    ...getAuthHeaders(),
    'x-gutdb-token': token,
    'x-opencode-directory': encodeOpenCodeDirectoryHeader(directory),
  }
  let restart = options.restart === true
  // A result preview can be rebuilt from saved artifacts. First let the server
  // reuse/reconcile the existing job; only its terminal receipt permits restart.
  const autoRestartEnded = options.autoRestartEnded === true && !restart
  let restartAfterReconciliation = restart || autoRestartEnded
  // A controller that lost its compute allocation is replaced by the server on the next open; ask once.
  let controlLostRetried = false
  for (;;) {
    checkWaiting()
    const payload = { path, scope: 'workspace', basePath: '/opencode', asynchronous: true, restart }
    if (options.standardResult) payload.standardResult = options.standardResult
    // A lost response must not leave permission to restart a future generation.
    restart = false
    let response
    try {
      response = await axios.post('/opencode/marimo/session', payload, {
        headers, signal, timeout: Math.max(1, Math.min(requestTimeoutMs, deadline - Date.now())),
      })
    } catch (cause) {
      checkWaiting()
      const code = cause.response?.data?.code
      // Rebuild once after confirmed termination, never on a missing response,
      // a scheduler outage, or an ownership error. Do not delay a known restart
      // by the polling interval intended for jobs that are still preparing.
      if (code === 'NOTEBOOK_RESTART_REQUIRED' && restartAfterReconciliation) {
        restartAfterReconciliation = false
        restart = true
        continue
      } else if (isRetryableNotebookTransportError(cause) || code === 'NOTEBOOK_CONNECTION_UNCERTAIN') {
        // The bridge is restoring the compute connection; keep waiting within the same deadline.
        options.onRuntime?.({ state: 'recovering' })
      } else if (code === 'NOTEBOOK_CONTROL_LOST' && !controlLostRetried) {
        controlLostRetried = true
        options.onRuntime?.({ state: 'recovering' })
        continue
      } else {
        throw cause
      }
    }
    checkWaiting()
    if (response) {
      const data = response.data || {}
      if (response.status !== 202 && !data.preparing) {
        if (!data.url) throw new Error('Notebook 会话地址不可用')
        return response
      }
      options.onRuntime?.(data.runtime)
      // An automatic open may still be reconciling the old job, even if its
      // cached state says running. Its one restart is consumed only on a
      // terminal receipt; an explicit restart is consumed once accepted.
      if (!autoRestartEnded && ['reserved', 'submitting', 'pending', 'starting', 'running'].includes(data.runtime?.state)) {
        restartAfterReconciliation = false
      }
    }
    if (pollIntervalMs > 0) await wait(Math.min(pollIntervalMs, deadline - Date.now()), signal)
  }
}

const isRetryableNotebookTransportError = cause => !cause?.response?.data?.code && (
  RETRYABLE_INTERACTIVE_CODE.has(cause?.code) ||
  [408, 425, 429, 502, 503, 504].includes(cause?.response?.status) ||
  Boolean(cause?.request && !cause?.response)
)

// 公共示例数据：当前流程下已上架、且兼容指定流程版本的示例清单。
// 总开关关闭或没有可用的示例时，后端返回空数组（不报错）。
export const listDemoDatasets = (templateSlug, templateVersion = '') =>
  axios.get('/api/inspiration/demo-datasets', {
    headers: getAuthHeaders(),
    params: { templateSlug, ...(templateVersion ? { templateVersion } : {}) },
  })
